import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { ShoppingBag, X } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { CartPanel } from "@/components/cart/CartPanel";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { usePresence } from "@/hooks/use-presence";

/** Floating cart shortcut and drawer on menu pages. */
export function MobileCartBar() {
  const { t } = useI18n();
  const { lineCount } = useCart();
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const orderingPage = pathname === "/menu" || pathname.startsWith("/menu/");
  const visible = lineCount > 0 && orderingPage;
  const barPresence = usePresence(visible, 200);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  if (!barPresence.rendered) return null;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <div
        className="motion-cart-bar pointer-events-none fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-40 md:bottom-6 md:right-6"
        data-presence={barPresence.phase}
        aria-hidden={barPresence.phase === "exiting"}
        inert={barPresence.phase === "exiting"}
      >
        <DrawerTrigger asChild>
          <button
            type="button"
            aria-label={`${t("cart.view")} (${lineCount})`}
            className="pressable pointer-events-auto relative grid size-14 place-items-center rounded-full border-2 border-gold bg-primary text-primary-foreground shadow-lift ring-2 ring-background hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <ShoppingBag className="size-6" aria-hidden="true" />
            <span aria-hidden="true" className="absolute -right-1 -top-1 grid min-h-6 min-w-6 place-items-center rounded-full border-2 border-background bg-foreground px-1 text-[10px] font-black leading-none tabular-nums text-background">
              {lineCount > 99 ? "99+" : lineCount}
            </span>
          </button>
        </DrawerTrigger>
      </div>

      <DrawerContent className="max-h-[88dvh]">
        <DrawerHeader className="flex items-center justify-between pb-0 text-start">
          <div>
            <DrawerTitle>{t("cart.title")}</DrawerTitle>
            <DrawerDescription className="sr-only">{t("checkout.subtitle")}</DrawerDescription>
          </div>
          <DrawerClose asChild><Button variant="ghost" size="icon" aria-label={t("common.close")}><X /></Button></DrawerClose>
        </DrawerHeader>
        <div className="overflow-y-auto px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-3">
          <CartPanel showHeader={false} onCheckout={() => setOpen(false)} />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
